import React, { useEffect } from 'react';
import { InputText, TextArea, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { useHistory, useParams } from 'react-router-dom';
import { getRFQProposalChecklistURL } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import RFQChecklistOptionHeader from 'components/common/headers/RFQChecklistOptionHeader';
import { useRFQInitiationPageStore } from 'store/pages/rfq/initiate-rfq/rfq-initiation/pageStore';
import PageFooter from 'components/common/general/layouts/PageFooter';
import RoundedWrapper from 'components/common/rfq/RoundedWrapper';
import { InputRowStyled, TitleStyled } from './styles';

interface Props {
  title: string;
  text: string;
  showResponseDueDate: boolean;
}

const InitiateRFQContent: React.FC<Props> = observer(({ title, text, showResponseDueDate }) => {
  const history = useHistory();

  const query = useQueryParams();
  const cartId = query.get('cartId') || '';
  const { periodId } = useParams<{ periodId?: string }>();

  const {
    isLoading,
    initiateRFQForm,
    groupId,
    getInitialData,
    updateProposalAnswers,
    onSaveProposal,
    setRequestDueDate,
  } = useRFQInitiationPageStore();

  useEffect(() => {
    void getInitialData(cartId, periodId);
    if (!showResponseDueDate) setRequestDueDate('2026-01-01'); // Set a default response due date
  }, []);

  // If the URL includes a period ID, it indicates that the 'Initiate RFQ' page was opened from the checklist.
  const onDefaultRedirect = (): void => {
    if (groupId) {
      history.push(periodId ? getRFQProposalChecklistURL({ periodId, cartId }) : `/group/${groupId}`);
    }
  };

  const onSaveStep = async (): Promise<void> => {
    if (periodId) {
      await updateProposalAnswers(periodId);
      onDefaultRedirect();
    } else {
      // Save the proposal and retrieve the period ID to enable redirection to the checklist,
      // marking this step as completed.
      const newPeriod = await onSaveProposal();
      const rfqChecklistUrl = getRFQProposalChecklistURL({ periodId: newPeriod, cartId });
      history.push(rfqChecklistUrl);
    }
  };

  return (
    <>
      <RFQChecklistOptionHeader title={title} subtitle={text} onRedirectBack={onDefaultRedirect} />
      <RoundedWrapper loading={isLoading}>
        <TitleStyled>Basic Information</TitleStyled>
        <InputRowStyled>
          <InputText
            label="Proposal Name"
            name="proposalName"
            id="proposalName"
            value={initiateRFQForm.fields.proposalName.value}
            onChange={initiateRFQForm.onFieldChange}
            validationMessage={initiateRFQForm.fields.proposalName.unpassedValidationMsg || ''}
            validationPassed={!initiateRFQForm.fields.proposalName.unpassedValidationMsg}
            width={showResponseDueDate ? '65%' : '100%'}
          />
          {showResponseDueDate && (
            <InputText
              label="Response Due Date"
              name="responseDueDate"
              id="responseDueDate"
              htmlType="date"
              value={initiateRFQForm.fields.responseDueDate.value}
              onChange={initiateRFQForm.onFieldChange}
              validationMessage={initiateRFQForm.fields.responseDueDate.unpassedValidationMsg || ''}
              validationPassed={!initiateRFQForm.fields.responseDueDate.unpassedValidationMsg}
              width="35%"
            />
          )}
        </InputRowStyled>
        <TextArea
          label="Instructions"
          maxLength={500}
          name="notes"
          id="notes"
          width="100%"
          value={initiateRFQForm.fields.notes.value}
          onChange={initiateRFQForm.onFieldChange}
          validationPassed={!initiateRFQForm.fields.notes.unpassedValidationMsg}
          validationMessage={initiateRFQForm.fields.notes.unpassedValidationMsg}
        />
      </RoundedWrapper>
      <PageFooter
        completeButtonText={periodId ? 'Update' : 'Complete'}
        disableCompleteButton={isLoading || !initiateRFQForm.isFormValid()}
        onComplete={onSaveStep}
        onBack={onDefaultRedirect}
      />
    </>
  );
});

export default InitiateRFQContent;
